import { useEffect, useState } from "react";
import { BANNER_CONFIG } from "~/config/navigation";
import { ArrowCircle, Close } from "~/components/icons";

/**
 * Promotional banner above the header.
 * Matches Hugo: themes/conductorone/layouts/partials/banner.html
 *
 * Dismissal is persisted per-campaign in localStorage keyed on
 * BANNER_CONFIG.id, so hiding it survives reloads but a new campaign
 * (new id) shows again.
 */
const DISMISS_KEY = `c1-banner-dismissed-${BANNER_CONFIG.id}`;

export function Banner() {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") setDismissed(true);
    } catch {
      // localStorage unavailable (private mode / SSR) — leave banner shown.
    }
  }, []);

  if (!BANNER_CONFIG.enabled || dismissed) return null;

  const isExternal = BANNER_CONFIG.link.startsWith("http");
  const openInNewTab = isExternal && !BANNER_CONFIG.openInSameTab;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // Ignore write failures; the banner stays hidden for this session.
    }
  };

  return (
    <div className="relative z-50 bg-[#FF92EB] px-10 py-3 text-center text-xs font-semibold leading-tight text-charcoal md:text-sm">
      <a
        href={BANNER_CONFIG.link}
        {...(openInNewTab ? { target: "_blank", rel: "noopener" } : {})}
        className={`flex items-center justify-center gap-x-1.5 ${
          BANNER_CONFIG.logo ? "md:flex-wrap" : "flex-wrap"
        }`}
      >
        {BANNER_CONFIG.logo && (
          <img
            src={BANNER_CONFIG.logo.src}
            width={BANNER_CONFIG.logo.width}
            height={BANNER_CONFIG.logo.height}
            alt=""
            className="mr-1 h-5 w-auto shrink-0 md:mr-1.5 md:h-6"
          />
        )}
        <span className="hidden md:block">{BANNER_CONFIG.copyDesktop}</span>
        <span className="block md:hidden">
          {BANNER_CONFIG.copyMobile}
        </span>
        {BANNER_CONFIG.cta && (
          <div className="hidden items-center gap-x-2 md:inline-flex">
            <span aria-hidden="true">•</span>
            <span className="font-medium underline">{BANNER_CONFIG.cta}</span>
            <ArrowCircle className="w-4" />
          </div>
        )}
      </a>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss banner"
        className="absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded text-charcoal/70 transition-colors hover:text-charcoal focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-charcoal/70 md:right-2"
      >
        <Close className="h-5 w-5" />
      </button>
    </div>
  );
}
